import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getPutDetail, wijzigPut, buitenGebruikPut, getPutGebiedsstatus, getGebieden, getKeurkaartCheck, verstuurVeldwerkLink, verstuurAfdichtingsverzoek, startAfdichtingsprocedure, stopAfdichtingsprocedure } from '../api/api';
import VerversPill from '../components/VerversPill';
import { usePresence, PresenceLabel } from '../components/usePresence';
import { useAuth } from '../components/AuthContext';
import Modal from '../components/Modal';
import { fmtId, fmtDatumNL } from '../util/format';
import Klokje from '../components/Klokje';
import KaartMetLegenda from '../components/KaartMetLegenda';
import { gebiedStijl } from '../util/gebiedstijl';
import OpmerkingenVeld from '../components/OpmerkingenVeld';
import { updatePutOpmerkingen } from '../api/api';

/* Hoe we weten dat een put geboord is, onder het veld Boorstatus. Een
   gemeten diepte of een aanlegdatum spreekt voor zich; een bevestiging noemt
   de melding waarin de melder het zei, als link, of alleen de dag als een
   behandelaar het met de hand heeft gezet. */
function BoorstatusBron({ put, editing }) {
  let tekst = null;
  if (put.aanleg_datum) tekst = <>Aanlegdatum bekend</>;
  else if (put.gemeten_diepte_m != null) tekst = <>Gemeten diepte bekend</>;
  else if (put.geboord_bevestigd_op) {
    const v = put.bevestiging_verwijzing;
    const nr = put.geboord_bevestigd_kenmerk;
    tekst = (
      <>
        Bevestigd{nr ? <> in melding {v
          ? <Link to={`/beregening/inrichtingen/${v.inrichting_id}/${v.soort}/${v.id}`}>{nr}</Link>
          : <strong>{nr}</strong>}</> : ' door een behandelaar'} op {fmtDatumNL(put.geboord_bevestigd_op)}
        {editing && '. Kies je hier iets anders, dan vervalt die bevestiging'}
      </>
    );
  } else if (put.nog_te_boren) tekst = <>Voor zover bekend nog niet geboord</>;
  if (!tekst) return null;
  return <span className="veld-bron">{tekst}</span>;
}

function Field({ label, value, editing, onChange, type = 'text' }) {
  if (!editing) {
    const leeg = value === null || value === undefined || value === '';
    // Lezen in DD maand JJJJ; het ISO-formaat hoort bij het invoerveld.
    const toon = leeg ? '—' : (type === 'date' ? fmtDatumNL(value) : value);
    return (
      <div className="field">
        <span className="field-label">{label}</span>
        <span className={`field-value ${leeg ? 'empty' : ''}`}>
          {toon}
        </span>
      </div>
    );
  }
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      {type === 'textarea' ? (
        <textarea rows={3} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} step="any" value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export default function PutDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [buitenGebruikOpen, setBuitenGebruikOpen] = useState(false);
  const [buitenGebruikDatum, setBuitenGebruikDatum] = useState(new Date().toISOString().slice(0, 10));
  const [gebiedsstatus, setGebiedsstatus] = useState(null);
  const [gebiedGeometrieen, setGebiedGeometrieen] = useState([]);
  const [keurkaartLagen, setKeurkaartLagen] = useState({ bijlage11: null, bijlage12: null });

  const { gebruiker } = useAuth();
  const presenceAnderen = usePresence('put', id, editing, gebruiker?.naam);

  // Acties-popover
  const [actiesOpen, setActiesOpen] = useState(false);
  const actiesRef = useRef(null);
  // Veldwerk-link mail
  const [veldwerkOpen, setVeldwerkOpen] = useState(false);
  const [veldwerkInrichting, setVeldwerkInrichting] = useState('');
  const [veldwerkBezig, setVeldwerkBezig] = useState(false);
  const [veldwerkResult, setVeldwerkResult] = useState(null);
  const [afdichtOpen, setAfdichtOpen] = useState(false);
  const [afdichtBezig, setAfdichtBezig] = useState(false);
  const [afdichtResult, setAfdichtResult] = useState(null);
  // Markering "afdichtingsprocedure gestart": één dialoog voor zetten en voor
  // intrekken; welke van de twee staat in procedureActie.
  const [procedureActie, setProcedureActie] = useState(null);   // 'starten' | 'intrekken'
  const [procedureBezig, setProcedureBezig] = useState(false);
  // Waar komt het bericht vandaan dat deze put eruit gaat? Verplicht bij
  // starten; het register bewaart de regel in de opmerkingen, niet als veld.
  const [procedureHerkomst, setProcedureHerkomst] = useState('');

  const laad = useCallback(async () => {
    try {
      const res = await getPutDetail(id);
      setData(res.data);
      setForm(res.data.put);
      // Keurkaart-lagen (bijlage 11 + 12) ophalen op put-coördinaten, parallel.
      // Read-only referentielookup; falen mag stil — pill toont dan '—'.
      const x = res.data.put.x_rd;
      const y = res.data.put.y_rd;
      if (x != null && y != null) {
        Promise.all([
          getKeurkaartCheck(x, y, 11).catch(() => ({ data: null })),
          getKeurkaartCheck(x, y, 12).catch(() => ({ data: null })),
        ]).then(([b11, b12]) => {
          setKeurkaartLagen({ bijlage11: b11.data, bijlage12: b12.data });
        });
      }
      // Gebiedsstatus parallel
      const gs = await getPutGebiedsstatus(id);
      setGebiedsstatus(gs.data);
      // Alle gebieden ophalen voor overlay (niet alleen de getroffen)
      const alle = await getGebieden(undefined, true);
      setGebiedGeometrieen(alle.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [id]);

  // Lichte refresh: alleen de put-data + gebiedsstatus opnieuw ophalen.
  // Bewust NIET de zware gebieden-overlay (getGebieden) — die is bij het
  // openen geladen en verandert zelden; zo blijft verversen snel. De oude
  // data blijft zichtbaar tot de nieuwe binnen is (geen leegspringen).
  const lichteRefresh = useCallback(async () => {
    const res = await getPutDetail(id);
    setData(res.data);
    setForm(res.data.put);
    const gs = await getPutGebiedsstatus(id);
    setGebiedsstatus(gs.data);
  }, [id]);

  useEffect(() => { laad(); }, [laad]);

  // Stabiele referentie voor de RDKaart-punten zodat de useEffect
  // in RDKaart niet bij elke re-render opnieuw vuurt en de kaart
  // dus niet 3x achter elkaar herlaadt.
  const kaartPunten = useMemo(() => {
    if (!data?.put) return [];
    const put = data.put;
    return [{
      put_id: put.put_id,
      x_rd: put.x_rd,
      y_rd: put.y_rd,
      perceel: put.perceel,
      afgedicht: !!put.buiten_gebruik_datum,
    }];
  }, [data?.put]);

  const update = (k, v) => setForm({ ...form, [k]: v });
  // Een gemeten diepte of een aanlegdatum in het formulier zet de boorstatus
  // vast op Geboord; zie de keuzelijst onder "Aanleg en afdichting".
  const _leegVeld = (v) => v === null || v === undefined || v === '';
  const vastGeboord = !_leegVeld(form.gemeten_diepte_m) || !_leegVeld(form.aanleg_datum);

  const opslaan = async () => {
    setBezig(true);
    setError(null);
    try {
      const payload = { ...form };
      delete payload.put_id;
      ['x_rd', 'y_rd', 'opgegeven_diepte_m', 'gemeten_diepte_m', 'max_diepte_mv', 'diepte_bv_filter_m', 'filterlengte_m', 'maaiveldhoogte_nap_m'].forEach(k => {
        if (payload[k] === '' || payload[k] === null) {
          payload[k] = null;
        } else {
          payload[k] = parseFloat(payload[k]);
        }
      });
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (payload.x_rd === null || payload.y_rd === null) {
        throw new Error('X en Y coördinaat zijn verplicht');
      }
      /* De boorstatus gaat alleen mee als hij hier is veranderd. Ongewijzigd
         meesturen zou de bevestiging van de melder vervangen door een
         bevestiging van vandaag zonder melding erbij. De afgeleide en
         alleen-lezen velden gaan er ook uit; de backend kent ze niet. */
      if (payload.boorstatus_code === put.boorstatus_code) delete payload.boorstatus;
      else payload.boorstatus = payload.boorstatus_code;
      // Een gemeten diepte of een aanlegdatum maakt de put geboord; de
      // backend leidt dat af en de keuze hierboven staat dan op slot.
      if (payload.gemeten_diepte_m != null || payload.aanleg_datum) delete payload.boorstatus;
      ['boorstatus_code', 'diepte', 'diepte_bron', 'nog_te_boren',
       'geboord_bevestigd_op', 'geboord_bevestigd_kenmerk', 'herkomst_verwijzing',
       'bevestiging_verwijzing'].forEach(k => { delete payload[k]; });
      if (payload.boorstatus === undefined) delete payload.boorstatus;
      payload.verwachte_gewijzigd_op = form.gewijzigd_op || null;
      delete payload.gewijzigd_op;
      delete payload.aangemaakt_op;
      await wijzigPut(id, payload);
      setEditing(false);
      laad();
    } catch (e) {
      if (e.response?.status === 409) {
        setEditing(false);
        setError('Een collega heeft deze put zojuist gewijzigd. De pagina is opnieuw geladen met de actuele gegevens.');
        lichteRefresh();
      } else {
        setError(e.response?.data?.detail || e.message);
      }
    } finally {
      setBezig(false);
    }
  };

  const buitenGebruik = async () => {
    try {
      await buitenGebruikPut(id, buitenGebruikDatum);
      setBuitenGebruikOpen(false);
      laad();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // Acties-popover sluiten bij klik buiten of Escape
  useEffect(() => {
    if (!actiesOpen) return;
    const opKlik = (e) => { if (actiesRef.current && !actiesRef.current.contains(e.target)) setActiesOpen(false); };
    const opToets = (e) => { if (e.key === 'Escape') setActiesOpen(false); };
    document.addEventListener('mousedown', opKlik);
    document.addEventListener('keydown', opToets);
    return () => {
      document.removeEventListener('mousedown', opKlik);
      document.removeEventListener('keydown', opToets);
    };
  }, [actiesOpen]);

  // Actieve (huidige) koppelingen, langst-gekoppelde eerst. De backend levert
  // gekoppelde_inrichtingen al gesorteerd (actief eerst, dan begin_datum oplopend),
  // dus de eerste actieve is de langst gekoppelde.
  const actieveInrichtingen = useMemo(
    () => (data?.gekoppelde_inrichtingen || []).filter(i => i.actief),
    [data]
  );

  /* Aan wie mag je de boorgegevens vragen? Normaal de houder van een lopend
     recht. Hangt de put aan niets, dan de houder van het recht dat eraan komt:
     die gaat 'm boren. Nooit allebei — zolang er een lopend recht is, is dat
     de enige die erover gaat. */
  const veldwerkKeuzes = useMemo(() => {
    if (actieveInrichtingen.length > 0) return actieveInrichtingen;
    return (data?.aankomende_koppelingen || []).filter(a => a.inrichting_id);
  }, [actieveInrichtingen, data]);
  const veldwerkToekomstig = actieveInrichtingen.length === 0 && veldwerkKeuzes.length > 0;

  // Laatste houder = afgesloten koppeling met de HOOGSTE eind_datum. Dit moet
  // exact overeenkomen met wat de backend (verstuur_afdichtingsverzoek) kiest,
  // zodat de modal dezelfde houder toont die de mail daadwerkelijk krijgt.
  const laatsteHouder = useMemo(() => {
    const afgesloten = (data?.gekoppelde_inrichtingen || [])
      .filter(i => !i.actief && i.eind_datum_koppeling);
    if (afgesloten.length === 0) return null;
    return afgesloten.reduce((beste, i) =>
      (i.eind_datum_koppeling > beste.eind_datum_koppeling) ? i : beste
    );
  }, [data]);

  // Bij openen van de dialoog: autoselect de langst-gekoppelde actieve inrichting
  useEffect(() => {
    if (veldwerkOpen && veldwerkKeuzes.length > 0) {
      setVeldwerkInrichting(prev => prev || veldwerkKeuzes[0].inrichting_id);
    }
  }, [veldwerkOpen, veldwerkKeuzes]);

  const verstuurAfdicht = async () => {
    setAfdichtBezig(true);
    setAfdichtResult(null);
    try {
      const res = await verstuurAfdichtingsverzoek(id);
      setAfdichtOpen(false);
      setAfdichtResult({ ok: true, ...res.data });
    } catch (e) {
      setAfdichtResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setAfdichtBezig(false);
    }
  };

  const zetProcedure = async () => {
    setProcedureBezig(true);
    setError(null);
    try {
      if (procedureActie === 'starten') await startAfdichtingsprocedure(id, procedureHerkomst);
      else await stopAfdichtingsprocedure(id);
      setProcedureActie(null);
      laad();
    } catch (e) {
      setProcedureActie(null);
      setError(e.response?.data?.detail || e.message);
    } finally {
      setProcedureBezig(false);
    }
  };

  const verstuurVeldwerk = async () => {
    setVeldwerkBezig(true);
    setVeldwerkResult(null);
    try {
      const res = await verstuurVeldwerkLink(id, veldwerkInrichting);
      setVeldwerkOpen(false);
      setVeldwerkResult({ ok: true, ...res.data });
    } catch (e) {
      setVeldwerkResult({ ok: false, error: e.response?.data?.detail || e.message });
    } finally {
      setVeldwerkBezig(false);
    }
  };

  if (!data) return <div className="empty-state"><span className="spinner" /></div>;

  const { put, gekoppelde_inrichtingen, aankomende_koppelingen: aankomend } = data;
  const isActief = !put.buiten_gebruik_datum;
  // Veldwerk valt op te vragen zolang minstens één van de drie boorgegevens
  // (diepte, filterdiepte, boordatum) nog ontbreekt. Zijn ze alle drie gevuld,
  // dan is er niets meer op te vragen en verbergen we de actie.
  const _leeg = (v) => v === null || v === undefined || v === '';
  const alleGegevensBekend =
    !_leeg(put.gemeten_diepte_m) && !_leeg(put.diepte_bv_filter_m) &&
    !_leeg(put.filterlengte_m) && !_leeg(put.maaiveldhoogte_nap_m) && !_leeg(put.aanleg_datum);

  /* Wat houdt de afdichtingsprocedure tegen?

     Een lopende koppeling, want dan mag iemand deze put nog gebruiken. Maar
     ook een recht dat nog moet ingaan: dat pakt de put op zijn ingangsdatum
     alsnog op, en die koppeling zou dan stuklopen op de procedure. Allebei
     dus, en allebei met naam en toenaam — de behandelaar moet weten wélke
     inrichting hij eerst moet afhandelen. De backend bewaakt hetzelfde; dit
     is de uitleg vooraf, geen vervanging van die controle. */
  const blokkerendActief = actieveInrichtingen;
  const blokkerendAankomend = aankomend || [];
  const totaalBlokkerend = blokkerendActief.length + blokkerendAankomend.length;
  const startGeblokkeerd = totaalBlokkerend > 0;

  return (
    <div className="main-inner">
      <div className="detail-layout">
        <div className="detail-header page-header">
        <div>
          <h1 className="page-title">{fmtId(put.put_id)}</h1>
          <p className="page-subtitle">
            {Math.round(put.x_rd)}, {Math.round(put.y_rd)} (RD)
            {!isActief && ` · afgedicht per ${fmtDatumNL(put.buiten_gebruik_datum)}`}
          </p>
          {isActief && put.afdichting_gestart_datum && (
            <span className="chip chip-warning">
              Afdichtingsprocedure gestart op {fmtDatumNL(put.afdichting_gestart_datum)}
            </span>
          )}
          {/* Alleen bij "Nog te boren". Dat is de enige boorstatus die
              verandert wat je moet doen; "Onbekend" en "Geboord" verklaren
              zichzelf in de kaart eronder en horen niet bovenaan te staan. */}
          {put.boorstatus === 'Nog te boren' && (
            <span className="chip chip-warning">Nog te boren</span>
          )}
        </div>
        <div className="row">
          <PresenceLabel anderen={presenceAnderen} />
          {!editing && <VerversPill onVervers={lichteRefresh} />}
          {!editing ? (
            <div className="acties-menu-wrap" ref={actiesRef} style={{ position: 'relative', display: 'inline-block' }}>
              <button className="btn btn-secondary" onClick={() => setActiesOpen(o => !o)}>
                Acties
                <svg
                  width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ marginLeft: 2, transition: 'transform .15s', transform: actiesOpen ? 'rotate(180deg)' : 'none' }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {actiesOpen && (
                <div className="acties-popover">
                  <button className="acties-item" onClick={() => { setActiesOpen(false); setEditing(true); }}>
                    Bewerken
                  </button>
                  {!alleGegevensBekend && (
                    <button
                      className="acties-item"
                      onClick={() => { setActiesOpen(false); setVeldwerkResult(null); setVeldwerkOpen(true); }}
                    >
                      Veldwerkgegevens opvragen
                    </button>
                  )}
                  {/* De markering dat de put eruit gaat. Losstaand van de mail
                      hieronder: bij een put uit de datamigratie is er geen houder
                      om iets aan te vragen, en de procedure loopt toch.

                      Kan het niet, dan verdwijnt de actie niet maar staat hij
                      grijs. Een verdwenen menu-item laat je zoeken naar iets wat
                      er niet is; een grijs item vertelt dat het bestaat en, na een
                      klik, waarom het nu niet kan. Daarom is deze knop bewust niet
                      disabled: een disabled button vangt geen klik. */}
                  {isActief && (
                    put.afdichting_gestart_datum ? (
                      <button
                        className="acties-item"
                        onClick={() => { setActiesOpen(false); setProcedureActie('intrekken'); }}
                      >
                        Afdichtingsprocedure intrekken
                      </button>
                    ) : (
                      <button
                        className="acties-item"
                        style={startGeblokkeerd ? { color: 'var(--text-muted)' } : undefined}
                        title={startGeblokkeerd ? 'Kan nu niet — klik voor de reden' : undefined}
                        onClick={() => {
                          setActiesOpen(false);
                          setProcedureHerkomst('');
                          setProcedureActie(startGeblokkeerd ? 'geblokkeerd' : 'starten');
                        }}
                      >
                        Afdichtingsprocedure starten
                      </button>
                    )
                  )}
                  {/* Alleen als er iemand is om het aan te vragen. Een put die
                      nooit gekoppeld is geweest heeft geen vorige houder, en dan
                      opent deze knop een dialoog zonder ontvanger. */}
                  {isActief && actieveInrichtingen.length === 0 && laatsteHouder && (() => {
                    const st = put.afdicht_status?.status;
                    if (st === 'verstuurd_recent') {
                      return (
                        <button
                          className="acties-item"
                          disabled
                          style={{ opacity: 0.5, cursor: 'default' }}
                          title="Afdichtingsverzoek recent verstuurd; een herinnering kan na 28 dagen."
                        >
                          Afdichtingsverzoek verzonden op {fmtDatumNL(put.afdicht_status.verzonden_op)}
                        </button>
                      );
                    }
                    const label = st === 'herinnering_mogelijk' ? 'Herinnering afdichtingsverzoek versturen' : 'Afdichtingsverzoek versturen';
                    return (
                      <button
                        className="acties-item"
                        onClick={() => { setActiesOpen(false); setAfdichtResult(null); setAfdichtOpen(true); }}
                      >
                        {label}
                      </button>
                    );
                  })()}
                  {isActief && (
                    <>
                      <div className="acties-divider" />
                      <button
                        className="acties-item acties-item-danger"
                        onClick={() => { setActiesOpen(false); setBuitenGebruikOpen(true); }}
                      >
                        Afdichten
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => { setEditing(false); setForm(put); }}>Annuleren</button>
              <button className="btn btn-primary" onClick={opslaan} disabled={bezig}>
                {bezig ? <><span className="spinner" /> Opslaan...</> : 'Opslaan'}
              </button>
            </>
          )}
        </div>
        </div>

        <div className="detail-main">

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header"><h3 className="card-title">Locatie</h3></div>
        <div className="field-grid cols-3">
          {editing ? (
            <>
              <Field label="X (RD)" value={form.x_rd} editing={editing} onChange={(v) => update('x_rd', v)} type="number" />
              <Field label="Y (RD)" value={form.y_rd} editing={editing} onChange={(v) => update('y_rd', v)} type="number" />
            </>
          ) : (
            <div className="field">
              <span className="field-label">RD (x, y)</span>
              <span className={`field-value ${(form.x_rd == null || form.y_rd == null) ? 'empty' : ''}`}>
                {form.x_rd != null && form.y_rd != null ? `${form.x_rd}, ${form.y_rd}` : '—'}
              </span>
            </div>
          )}
          <Field label="Perceel" value={form.perceel} editing={editing} onChange={(v) => update('perceel', v)} />
          <div className="field">
            <span className="field-label">Max boordiepte (grasland en kleine onttrekkingen)</span>
            <span className={`field-value ${keurkaartLagen.bijlage11?.max_diepte_m == null ? 'empty' : ''}`}>
              {keurkaartLagen.bijlage11?.max_diepte_m != null
                ? `${keurkaartLagen.bijlage11.max_diepte_m} m`
                : '—'}
            </span>
          </div>
          <div className="field">
            <span className="field-label">Max boordiepte (overige onttrekkingen / gewasberegening)</span>
            <span className={`field-value ${keurkaartLagen.bijlage12?.max_diepte_m == null ? 'empty' : ''}`}>
              {keurkaartLagen.bijlage12?.max_diepte_m != null
                ? `${keurkaartLagen.bijlage12.max_diepte_m} m`
                : '—'}
            </span>
          </div>
        </div>

        {gebiedsstatus && (() => {
          const kwetsbaar = (gebiedsstatus.gebieden || []).filter(g => g.type !== 'ambtsgebied');
          const waterstaatswerken = gebiedsstatus.waterstaatswerken || [];
          if (kwetsbaar.length === 0 && waterstaatswerken.length === 0) {
            return (
              <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
                ✓ Geen overlap met geregistreerde gebieden.
              </div>
            );
          }
          const renderChips = (label, lijst) => (
            <div style={{ marginTop: 16 }}>
              <div className="field-label" style={{ marginBottom: 8 }}>{label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {lijst.map(g => {
                  const s = gebiedStijl(g.type);
                  return (
                    <span
                      key={g.gebied_id}
                      style={{
                        display: 'inline-block',
                        padding: '4px 11px',
                        borderRadius: 999,
                        fontSize: 12.5,
                        fontWeight: 500,
                        background: s.chipBg,
                        color: s.chipText,
                        border: `1px solid ${s.stroke}`,
                        whiteSpace: 'nowrap',
                      }}
                      title={g.naam}
                    >
                      {s.kort || g.naam}
                    </span>
                  );
                })}
              </div>
            </div>
          );
          return (
            <>
              {kwetsbaar.length > 0 && renderChips('Gelegen in kwetsbare gebieden', kwetsbaar)}
              {waterstaatswerken.length > 0 && renderChips('Gelegen in waterstaatswerken', waterstaatswerken)}
            </>
          );
        })()}

        <div style={{ marginTop: 16 }}>
          <KaartMetLegenda
            punten={kaartPunten}
            overlays={gebiedGeometrieen}
            hoogte={360}
          />
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Diepte</h3></div>
        <div className="field-grid cols-3">
          {/* Drie dieptes, elk met een eigen herkomst (migratie 47). Gemeten komt
              uit het veldwerkverslag of uit een boorstaat die een behandelaar
              hier overneemt; opgegeven is wat de melder zei. Een gemeten
              diepte maakt de put Geboord en haalt de dieptevraag uit het
              formulier: de melder ziet hem dan als vast gegeven. */}
          <div className="field">
            <Field label="Gemeten diepte" value={form.gemeten_diepte_m} editing={editing} onChange={(v) => update('gemeten_diepte_m', v)} type="number" />
            <span className="veld-bron">
              {editing || put.gemeten_diepte_m != null ? 'Uit boorstaat of veldwerkverslag' : 'Nog niet gemeten'}
            </span>
          </div>
          <div className="field">
            <Field label="Opgegeven diepte" value={form.opgegeven_diepte_m} editing={editing} onChange={(v) => update('opgegeven_diepte_m', v)} type="number" />
            {!editing && put.opgegeven_diepte_m != null && (
              <span className="veld-bron">Zoals opgegeven bij de melding, niet gemeten</span>
            )}
          </div>
          <Field label="Max diepte (m-mv)" value={form.max_diepte_mv} editing={editing} onChange={(v) => update('max_diepte_mv', v)} type="number" />
          <Field label="Filterdiepte (bovenkant)" value={form.diepte_bv_filter_m} editing={editing} onChange={(v) => update('diepte_bv_filter_m', v)} type="number" />
          <Field label="Maaiveldhoogte (m NAP)" value={form.maaiveldhoogte_nap_m} editing={editing} onChange={(v) => update('maaiveldhoogte_nap_m', v)} type="number" />
          <Field label="Filterlengte (m)" value={form.filterlengte_m} editing={editing} onChange={(v) => update('filterlengte_m', v)} type="number" />
          {(() => {
            // Onderkant = bovenkant filter + ingemeten filterlengte. Dit is de
            // juiste afleiding, niet werkelijke_diepte (die zandvang meerekent).
            const bv = form.diepte_bv_filter_m;
            const fl = form.filterlengte_m;
            const ok = (bv != null && bv !== '' && fl != null && fl !== '')
              ? Number(bv) + Number(fl)
              : null;
            return (
              <Field label="Onderkant filter (m-mv)" value={ok} editing={false} onChange={() => {}} />
            );
          })()}
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Aanleg en afdichting</h3></div>
        <div className="field-grid cols-3">
          {/* Afgeleid in de backend (boorstatus.py) uit aanlegdatum, gemeten
              diepte, bevestiging en de te-boren-vlag. Zetten doe je als één
              woord; de backend vertaalt dat naar de kolommen. Een gemeten
              diepte of een aanlegdatum wint altijd: dan is de keuze hier
              uitgeschakeld, want er valt niets meer te kiezen. Dat volgt het
              formulier, dus wie hierboven een gemeten diepte intypt ziet de
              keuze meteen op Geboord springen. */}
          <div className="field">
            <span className="field-label">Boorstatus</span>
            {editing ? (
              <select
                value={vastGeboord ? 'geboord' : (form.boorstatus_code || 'onbekend')}
                disabled={vastGeboord}
                onChange={(e) => update('boorstatus_code', e.target.value)}
              >
                <option value="geboord">Geboord</option>
                <option value="te_boren">Nog te boren</option>
                <option value="onbekend">Onbekend</option>
              </select>
            ) : (
              <span className={`field-value ${put.boorstatus === 'Onbekend' ? 'empty' : ''}`}>{put.boorstatus}</span>
            )}
            <BoorstatusBron put={put} editing={editing} />
          </div>
          <Field label="Datum aanleg" value={form.aanleg_datum} editing={editing} onChange={(v) => update('aanleg_datum', v)} type="date" />
          {/* De hele levensloop op één regel: geboren, procedure gestart, dicht. */}
          <Field label="Afdichtingsprocedure gestart" value={form.afdichting_gestart_datum} editing={false} onChange={() => {}} type="date" />
          <Field label="Afdichtdatum" value={form.buiten_gebruik_datum} editing={false} onChange={() => {}} type="date" />
        </div>

        {/* Waar deze put vandaan komt. Blijft staan nadat hij geboord is: dan is
            de vraag "waar kwam die vandaan" niet minder relevant maar juist
            blijvend. De verwijzing wijst naar de klaarstaande melding zolang die
            klaarstaat, en daarna naar de historiepagina van dat recht; die
            omslag gebeurt vanzelf, want de backend zoekt hem elke keer opnieuw
            op. Is er niets te vinden — bijvoorbeeld bij een ingetrokken melding
            — dan blijft het kenmerk als tekst staan. */}
        {put.eerst_gemeld_kenmerk && (
          <p className="put-herkomst">
            Eerst gemeld bij melding{' '}
            {put.herkomst_verwijzing ? (
              <Link to={`/beregening/inrichtingen/${put.herkomst_verwijzing.inrichting_id}`
                        + `/${put.herkomst_verwijzing.soort}/${put.herkomst_verwijzing.id}`}>
                {put.eerst_gemeld_kenmerk}
              </Link>
            ) : <strong>{put.eerst_gemeld_kenmerk}</strong>}
            {put.eerst_gemeld_op && `, meldingsdatum ${fmtDatumNL(put.eerst_gemeld_op)}`}.
          </p>
        )}
        {/* Ligt de put in een waterstaatswerk, dan is daar één omgevingsvergunning
            voor — één besluit voor het boren en behouden van díe put, ongeacht in
            hoeveel werken hij ligt. Vandaar één nummer, met de werken erboven
            zodat duidelijk is waar het over gaat. Naslag, geen voorwaarde. */}
        {(gebiedsstatus?.waterstaatswerken || []).length > 0 && (
          <div className="put-wsw">
            <div className="put-wsw-kop">Vergunning waterstaatswerken</div>
            <div className="put-wsw-pillen">
              {gebiedsstatus.waterstaatswerken.map((g) => {
                const st = gebiedStijl(g.type);
                return (
                  <span key={g.gebied_id} title={g.naam} style={{
                    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                    fontSize: 11.5, fontWeight: 500, lineHeight: 1.4,
                    background: st.chipBg, color: st.chipText,
                    border: `1px solid ${st.stroke}`, whiteSpace: 'nowrap',
                  }}>{st.kort || g.naam}</span>
                );
              })}
            </div>
            <Field label="Besluitnummer" value={form.besluitnummer_wsw} editing={editing}
              onChange={(v) => update('besluitnummer_wsw', v)} />
          </div>
        )}
      </div>

      {(() => {
        /* Eén tabel, één kop, een statuschip per regel. De volgorde loopt van
           toekomst naar verleden — dezelfde richting als de tijdlijn op de
           inrichtingspagina.

           Kenmerk en rechtstype staan er bewust niet bij. Een koppeling loopt
           tussen put en inrichting; welk gebruiksrecht daar wanneer bij hoorde
           wisselt binnen diezelfde inrichting, en de koppelrij weet dat niet.
           Wie het precies wil weten volgt de inrichting en leest daar de
           tijdlijn. */
        const huidig = gekoppelde_inrichtingen
          .filter((i) => i.actief)
          .sort((a, b) => a.begin_datum_koppeling.localeCompare(b.begin_datum_koppeling));
        const historisch = gekoppelde_inrichtingen
          .filter((i) => !i.actief)
          .sort((a, b) => b.begin_datum_koppeling.localeCompare(a.begin_datum_koppeling));
        const komend = aankomend || [];
        const totaal = komend.length + huidig.length + historisch.length;

        const Inrichting = ({ id: iid }) => (iid ? (
          <Link to={`/beregening/inrichtingen/${iid}`} className="mono">{fmtId(iid)}</Link>
        ) : <span className="mono">—</span>);
        const Houder = ({ hid, naam }) => (hid ? (
          <Link to={`/beregening/houders/${hid}`}>{naam || fmtId(hid)}</Link>
        ) : (naam || '—'));

        return (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Koppelingen</h3>
              <span className={`chip ${huidig.length > 0 ? 'chip-success' : ''}`}>{totaal}</span>
            </div>
            {totaal === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 24 }}>
                Deze put is nooit aan een inrichting gekoppeld geweest.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr><th>Status</th><th>Inrichting</th><th>Houder</th><th>Periode</th></tr>
                </thead>
                <tbody>
                  {komend.map((k, n) => (
                    <tr key={`k-${k.inrichting_id}-${k.vanaf}-${n}`}>
                      <td>
                        <span className="chip chip-toekomstig">
                          <Klokje grootte={12} />Aankomend
                        </span>
                      </td>
                      <td><Inrichting id={k.inrichting_id} /></td>
                      <td><Houder hid={k.houder_id} naam={k.houder_naam} /></td>
                      <td>vanaf {fmtDatumNL(k.vanaf)}</td>
                    </tr>
                  ))}
                  {huidig.map((i) => (
                    <tr key={`h-${i.inrichting_id}-${i.begin_datum_koppeling}`}>
                      <td><span className="chip chip-success">Huidig</span></td>
                      <td><Inrichting id={i.inrichting_id} /></td>
                      <td><Houder hid={i.houder_id} naam={i.houder_naam} /></td>
                      <td>sinds {fmtDatumNL(i.begin_datum_koppeling)}</td>
                    </tr>
                  ))}
                  {historisch.map((i) => (
                    <tr className="rij-historisch"
                      key={`o-${i.inrichting_id}-${i.begin_datum_koppeling}`}>
                      <td><span className="chip">Historisch</span></td>
                      <td><Inrichting id={i.inrichting_id} /></td>
                      <td><Houder hid={i.houder_id} naam={i.houder_naam} /></td>
                      <td>
                        {fmtDatumNL(i.begin_datum_koppeling)} t/m{' '}
                        {i.eind_datum_koppeling ? fmtDatumNL(i.eind_datum_koppeling) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}
        </div>

        <aside className="detail-aside">
          <OpmerkingenVeld
            initialValue={form.opmerkingen}
            onSave={async (tekst) => {
              await updatePutOpmerkingen(id, tekst || null);
              update('opmerkingen', tekst);
            }}
          />
        </aside>
      </div>

      <Modal
        isOpen={buitenGebruikOpen}
        onClose={() => setBuitenGebruikOpen(false)}
        title="Put afdichten"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setBuitenGebruikOpen(false)}>Annuleren</button>
            <button className="btn btn-danger" onClick={buitenGebruik}>Afdichten</button>
          </>
        }
      >
        <p style={{ marginTop: 0, marginBottom: 20, color: 'var(--text-soft)' }}>
          De put wordt gearchiveerd en alle koppelingen aan inrichtingen worden gesloten.
        </p>
        <div className="field">
          <label className="field-label">Datum afdichting</label>
          <input type="date" value={buitenGebruikDatum} onChange={(e) => setBuitenGebruikDatum(e.target.value)} />
        </div>
      </Modal>

      <Modal
        isOpen={!!procedureActie}
        onClose={() => setProcedureActie(null)}
        title={
          procedureActie === 'geblokkeerd' ? 'Afdichtingsprocedure kan niet starten'
            : procedureActie === 'intrekken' ? 'Afdichtingsprocedure intrekken'
              : 'Afdichtingsprocedure starten'
        }
        footer={procedureActie === 'geblokkeerd' ? (
          <button className="btn btn-primary" onClick={() => setProcedureActie(null)}>Sluiten</button>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => setProcedureActie(null)}>Annuleren</button>
            <button
              className="btn btn-primary"
              onClick={zetProcedure}
              disabled={procedureBezig || (procedureActie === 'starten' && !procedureHerkomst.trim())}
            >
              {procedureBezig
                ? <><span className="spinner" /> Bezig…</>
                : (procedureActie === 'intrekken' ? 'Intrekken' : 'Procedure starten')}
            </button>
          </>
        )}
      >
        {procedureActie === 'geblokkeerd' ? (
          <>
            <p style={{ marginTop: 0, marginBottom: 12, color: 'var(--text-soft)' }}>
              De afdichtingsprocedure kan niet worden gestart, omdat:
            </p>
            <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, color: 'var(--text-soft)' }}>
              {blokkerendActief.length > 0 && (
                <li style={{ marginBottom: 10 }}>
                  Put {fmtId(put.put_id)} nog is gekoppeld aan de volgende actieve
                  {blokkerendActief.length === 1 ? ' inrichting' : ' inrichtingen'}:
                  <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {blokkerendActief.map((i) => (
                      <li key={`blok-a-${i.inrichting_id}-${i.begin_datum_koppeling}`} style={{ marginBottom: 3 }}>
                        <Link to={`/beregening/inrichtingen/${i.inrichting_id}`} className="mono">
                          {fmtId(i.inrichting_id)}
                        </Link>
                        {i.houder_naam ? ` — ${i.houder_naam}` : ''}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
              {blokkerendAankomend.length > 0 && (
                <li>
                  Put {fmtId(put.put_id)} onderdeel is van de volgende aankomende
                  {blokkerendAankomend.length === 1 ? ' inrichting' : ' inrichtingen'}:
                  <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {blokkerendAankomend.map((k, n) => (
                      <li key={`blok-k-${k.inrichting_id || k.kenmerk}-${n}`} style={{ marginBottom: 3 }}>
                        {k.inrichting_id ? (
                          <Link to={`/beregening/inrichtingen/${k.inrichting_id}`} className="mono">
                            {fmtId(k.inrichting_id)}
                          </Link>
                        ) : (
                          /* Een klaarstaande melding voor een houder die nog geen
                             inrichting heeft: er is nog geen nummer om naar te
                             wijzen, alleen het kenmerk van de melding. */
                          <span className="mono">{k.kenmerk || 'nog aan te maken inrichting'}</span>
                        )}
                        {k.houder_naam ? ` — ${k.houder_naam}` : ''}
                        {k.vanaf ? ` · vanaf ${fmtDatumNL(k.vanaf)}` : ''}
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
            <p style={{ marginTop: 0, marginBottom: 0, color: 'var(--text-soft)' }}>
              {totaalBlokkerend === 1
                ? 'De koppeling met deze inrichting moet worden beëindigd voordat de afdichtingsprocedure kan worden gestart. Daarvoor is een nieuw gebruiksrecht (vergunning of melding) nodig waaraan deze put niet is toegevoegd.'
                : 'De koppelingen met deze inrichtingen moeten worden beëindigd voordat de afdichtingsprocedure kan worden gestart. Daarvoor is voor iedere gekoppelde inrichting een nieuw gebruiksrecht (vergunning of melding) nodig, waaraan deze put niet is toegevoegd.'}
            </p>
          </>
        ) : procedureActie === 'intrekken' ? (
          <>
            <p style={{ marginTop: 0, marginBottom: 12, color: 'var(--text-soft)' }}>
              De markering op put {fmtId(put.put_id)} wordt weggehaald
              {put.afdichting_gestart_datum ? <> (gestart op {fmtDatumNL(put.afdichting_gestart_datum)})</> : null}.
            </p>
            <p style={{ marginTop: 0, marginBottom: 0, color: 'var(--text-soft)' }}>
              De put doet daarna weer gewoon mee: hij staat weer op de kaart van
              het meldingsformulier en er kan weer een gebruiksrecht aan
              gekoppeld worden.
            </p>
          </>
        ) : (
          <>
            <p style={{ marginTop: 0, marginBottom: 12, color: 'var(--text-soft)' }}>
              Put {fmtId(put.put_id)} wordt gemarkeerd als een put waarvan de
              afdichtingsprocedure loopt. Vanaf dat moment:
            </p>
            <ul style={{ marginTop: 0, marginBottom: 12, paddingLeft: 20, color: 'var(--text-soft)' }}>
              <li style={{ marginBottom: 6 }}>staat de put niet meer op de kaart van het meldingsformulier, en waarschuwt die dus ook niet meer dat er een put binnen 20 meter ligt;</li>
              <li style={{ marginBottom: 6 }}>kan er geen gebruiksrecht meer aan gekoppeld worden;</li>
              <li>blijft de put wel gewoon in het register staan, met zijn geschiedenis.</li>
            </ul>
            <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--text-soft)' }}>
              De procedure eindigt door de afdichtdatum in te vullen. De markering
              is tot dat moment in te trekken.
            </p>
            {/* Verplicht. Over een half jaar is dit het enige dat nog vertelt
                waarom deze put uit de roulatie ging — en dat is precies wat je
                bij handhaving moet kunnen laten zien. Geen namen: die staan in
                de mailbox, niet in dit register. */}
            <div className="field">
              <label className="field-label">Aanleiding</label>
              <textarea
                rows={2}
                value={procedureHerkomst}
                onChange={(e) => setProcedureHerkomst(e.target.value)}
                placeholder="bijv. bericht ontvangen op 02-07-2026 in vergunningen@brabantsedelta.nl"
              />
              <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '6px 2px 0' }}>
                Waar komt het bericht vandaan, of op grond waarvan constateert u het
                zelf? Deze regel komt in de notities bij de put te staan. Vul hier
                geen namen of adressen in.
              </p>
            </div>
          </>
        )}
      </Modal>

      <Modal
        isOpen={veldwerkOpen}
        onClose={() => setVeldwerkOpen(false)}
        title="Veldwerkgegevens opvragen"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setVeldwerkOpen(false)}>Annuleren</button>
            <button
              className="btn btn-primary"
              onClick={verstuurVeldwerk}
              disabled={veldwerkBezig || !veldwerkInrichting}
            >
              {veldwerkBezig ? <><span className="spinner" /> Versturen…</> : 'Uitnodiging versturen'}
            </button>
          </>
        }
      >
        {veldwerkKeuzes.length === 0 ? (
          <p style={{ marginTop: 0, color: 'var(--text-soft)' }}>
            Aan deze put is op dit moment geen inrichting gekoppeld, en er staat ook geen gebruiksrecht klaar dat hem straks oppakt. Er is dus geen houder om de gegevens aan op te vragen.
          </p>
        ) : (
          <>
            <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--text-soft)' }}>
              Kies de houder aan wie u de boorgegevens van put {fmtId(put.put_id)} wilt opvragen. Deze ontvangt een e-mail met een beveiligde link (10 dagen geldig, eenmalig te gebruiken).
            </p>
            {veldwerkToekomstig && (
              <p className="alert alert-warning" style={{ marginTop: 0, marginBottom: 16 }}>
                Deze put valt nu onder geen enkel gebruiksrecht. De houder hieronder
                krijgt de put pas op de genoemde datum, maar is wel degene die hem
                gaat boren.
              </p>
            )}
            <div className="field">
              <label className="field-label">Inrichting / houder</label>
              <select value={veldwerkInrichting} onChange={(e) => setVeldwerkInrichting(e.target.value)}>
                {veldwerkKeuzes.map(i => (
                  <option key={i.inrichting_id} value={i.inrichting_id}>
                    {i.houder_naam || '(naam onbekend)'} — {fmtId(i.inrichting_id)}
                    {i.vanaf ? ` (vanaf ${fmtDatumNL(i.vanaf)})` : ''}
                  </option>
                ))}
              </select>
            </div>
            {(() => {
              const gekozen = veldwerkKeuzes.find(i => i.inrichting_id === veldwerkInrichting);
              if (!gekozen) return null;
              return (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '10px 2px 0' }}>
                  {gekozen.houder_emailadres
                    ? <>De uitnodiging gaat naar <strong>{gekozen.houder_emailadres}</strong>.</>
                    : <span style={{ color: '#dc2626' }}>Let op: deze houder heeft geen e-mailadres. Versturen lukt dan niet.</span>}
                </p>
              );
            })()}
          </>
        )}
      </Modal>

      <Modal
        isOpen={!!veldwerkResult}
        onClose={() => setVeldwerkResult(null)}
        title={veldwerkResult?.ok ? 'Uitnodiging verzonden' : 'Versturen mislukt'}
        footer={<button className="btn btn-primary" onClick={() => setVeldwerkResult(null)}>Sluiten</button>}
      >
        {veldwerkResult?.ok ? (
          <div>
            <p style={{ marginTop: 0 }}>De uitnodiging is verstuurd naar:</p>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '8px 0' }}>{veldwerkResult.ontvanger}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>De link is 10 dagen geldig.</p>
          </div>
        ) : (
          <div>
            <p style={{ marginTop: 0 }}>Er ging iets mis bij het versturen.</p>
            <p style={{ fontSize: 13, color: '#dc2626', fontFamily: 'monospace', margin: '8px 0' }}>{veldwerkResult?.error}</p>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={afdichtOpen}
        onClose={() => setAfdichtOpen(false)}
        title="Afdichtingsverzoek versturen"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setAfdichtOpen(false)}>Annuleren</button>
            <button
              className="btn btn-primary"
              onClick={verstuurAfdicht}
              disabled={afdichtBezig || !laatsteHouder?.houder_emailadres}
            >
              {afdichtBezig ? <><span className="spinner" /> Versturen…</> : 'Verzoek versturen'}
            </button>
          </>
        }
      >
        <p style={{ marginTop: 0, marginBottom: 16, color: 'var(--text-soft)' }}>
          Er wordt een afdichtingsverzoek gemaild naar de laatst bekende houder van put {fmtId(put.put_id)}. Deze ontvangt een e-mail met een beveiligde link waarmee de afdichtdatum kan worden doorgegeven.
        </p>
        {laatsteHouder ? (
          <div className="field" style={{ marginBottom: 12 }}>
            <label className="field-label">Laatste houder</label>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '2px 0 2px' }}>{laatsteHouder.houder_naam || '(naam onbekend)'}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
              {laatsteHouder.eind_datum_koppeling ? <>Laatst gekoppeld tot {fmtDatumNL(laatsteHouder.eind_datum_koppeling)} · {fmtId(laatsteHouder.inrichting_id)}</> : fmtId(laatsteHouder.inrichting_id)}
            </p>
          </div>
        ) : (
          <p style={{ fontSize: 13, color: '#dc2626', margin: '0 0 12px' }}>
            Er is geen eerdere houder gevonden voor deze put. Versturen is niet mogelijk.
          </p>
        )}
        {laatsteHouder && (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 4px' }}>
            {laatsteHouder.houder_emailadres
              ? <>De uitnodiging gaat naar <strong>{laatsteHouder.houder_emailadres}</strong>.</>
              : <span style={{ color: '#dc2626' }}>Let op: deze houder heeft geen e-mailadres. Versturen lukt dan niet.</span>}
          </p>
        )}
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '8px 0 0' }}>
          U kunt dit verzoek later opnieuw versturen als herinnering.
        </p>
      </Modal>

      <Modal
        isOpen={!!afdichtResult}
        onClose={() => setAfdichtResult(null)}
        title={afdichtResult?.ok ? 'Afdichtingsverzoek verzonden' : 'Versturen mislukt'}
        footer={<button className="btn btn-primary" onClick={() => setAfdichtResult(null)}>Sluiten</button>}
      >
        {afdichtResult?.ok ? (
          <div>
            <p style={{ marginTop: 0 }}>Het afdichtingsverzoek is verstuurd naar:</p>
            <p style={{ fontSize: 15, fontWeight: 600, margin: '8px 0' }}>{afdichtResult.ontvanger}</p>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>De link is 90 dagen geldig.</p>
          </div>
        ) : (
          <div>
            <p style={{ marginTop: 0 }}>Er ging iets mis bij het versturen.</p>
            <p style={{ fontSize: 13, color: '#dc2626', fontFamily: 'monospace', margin: '8px 0' }}>{afdichtResult?.error}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
